<template>
  <div>
    <!-- Loading -->
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
      <p class="mt-2 text-muted">Loading data...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="!items || items.length === 0" class="text-center py-5">
      <i
        class="fe fe-inbox text-muted"
        style="font-size: 48px; opacity: 0.6"
      ></i>
      <p class="mt-3 text-muted">Tidak ada data untuk ditampilkan.</p>
    </div>

    <!-- Horizontal Scroll with Arrows -->
    <div v-else class="horizontal-scroll-container position-relative">
      <button
        class="scroll-arrow left"
        @click="scrollLeft"
        :disabled="!canScrollLeft"
        aria-label="Scroll left"
      >
        <i class="fe fe-chevron-left"></i>
      </button>
      <button
        class="scroll-arrow right"
        @click="scrollRight"
        :disabled="!canScrollRight"
        aria-label="Scroll right"
      >
        <i class="fe fe-chevron-right"></i>
      </button>
      <div
        class="row flex-nowrap horizontal-scroll-wrapper"
        ref="scrollWrapper"
        @scroll="handleScroll"
        style="overflow-x: auto; overflow-y: visible; scroll-behavior: smooth"
      >
        <div
          class="col-lg-3 col-md-6 col-sm-12 mb-4"
          v-for="course in items"
          :key="course.id"
          @click.stop="$emit('open-detail', course, type)"
        >
          <!-- Card Component -->
          <div
            class="card h-100 shadow-sm border"
            style="cursor: pointer"
            @click="$emit('select', course)"
          >
            <!-- Image -->
            <div class="position-relative">
              <img
                :src="
                  course && course.cover_image_path
                    ? `${course.cover_image_path}`
                    : `https://dummyimage.com/300x200/FAFAFA/000000&text=${course.title || 'Course'}`
                "
                :alt="course.title"
                class="card-img-top"
                style="height: 180px; object-fit: cover"
              />

              <div class="position-absolute" style="top: 8px; right: 8px">
                <div
                  class="bg-white rounded shadow-sm p-1"
                  style="
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  "
                >
                  <img
                    :src="
                      course?.training?.provider?.provider_logo_path ||
                      'https://dummyimage.com/16x16/ffffff/000000&text=Provider+title'
                    "
                    :alt="course.provider_name"
                    style="height: 16px; object-fit: cover"
                  />
                </div>
              </div>

              <!-- Training Status -->
              <div class="position-absolute" style="top: 8px; left: 8px">
                <span :class="getTrainingStatusClass(course.status)">
                  {{ getApprovalStatusLabel(course.status) }}
                </span>
              </div>

              <!-- TNA Status & GAP Competencies -->
              <div
                v-if="course.difference != null"
                class="position-absolute d-flex gap-2"
                style="bottom: 8px; left: 8px"
              >
                <div class="d-flex badge bg-opacity-10 bg-white rounded gap-1">
                  <b-icon
                    icon="file-earmark-bar-graph"
                    style="font-size: 0.75rem"
                  ></b-icon>
                  Gap
                  <span
                    :class="
                      course.difference < 1
                        ? 'badge bg-success'
                        : 'badge bg-danger'
                    "
                  >
                    {{ course.difference }}
                  </span>
                </div>
                <div class="d-flex badge bg-opacity-10 bg-white rounded gap-1">
                  <b-icon
                    icon="star-fill"
                    class="text-success"
                    style="font-size: 0.75rem"
                  ></b-icon>
                  <span>
                    {{ course.proficiency_standard }}
                  </span>
                </div>
                <div class="d-flex badge bg-opacity-10 bg-white rounded gap-1">
                  <b-icon
                    icon="star-fill"
                    class="text-warning"
                    style="font-size: 0.75rem"
                  ></b-icon>
                  <span>
                    {{ course.proficiency }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Card Body -->
            <div class="card-body p-3">
              <!-- Badges Row -->
              <div
                v-if="course.competency != null"
                class="mb-2 d-flex flex-wrap gap-1"
              >
                <span class="badge-icon bg-danger-soft text-danger">
                  <i
                    :class="getTrainingTypeIcon(course.training_type)"
                    style="font-size: 10px"
                  ></i>
                </span>

                <span class="badge-custom bg-warning-soft text-warning">
                  {{
                    course.competency ||
                    course.competencies[0].role_competency ||
                    "-"
                  }}
                </span>

                <span class="badge-custom bg-info-soft text-info">
                  {{ course.competency || course.competencies[0].type || "-" }}
                </span>
              </div>

              <!-- Title -->
              <h6
                class="card-title font-weight-bold"
                style="font-size: 0.95rem; min-height: 10px"
              >
                {{ course.title }}
              </h6>

              <!-- Description -->
              <p
                v-if="course.description"
                class="card-text text-muted course-description"
              >
                {{ truncateText(course.description, 80) }}
              </p>
            </div>

            <!-- Footer -->
            <div
              v-if="type != 'gap_competencies' || isFooter"
              class="card-footer bg-white border-top px-3 py-2"
            >
              <div class="d-flex justify-content-between align-items-center">
                <div v-if="type != 'gap_competencies'" class="d-flex gap-3">
                  <!-- Duration -->
                  <div
                    class="d-flex align-items-center"
                  >
                    <i
                      class="fe fe-clock text-muted me-1"
                      style="font-size: 0.85rem"
                    ></i>
                    <small class="text-muted" style="font-size: 0.75rem">
                      {{ Number(course.hour || 0) }}h
                    </small>
                  </div>
                  <div
                    class="d-flex align-items-center"
                  >
                    <i
                      class="fe fe-dollar-sign text-muted me-1"
                      style="font-size: 0.85rem"
                    ></i>
                    <small class="text-muted" style="font-size: 0.75rem">
                      {{ course?.currency }} {{ course?.price }}
                    </small>
                  </div>
                </div>

                <div v-if="isFooter" class="d-flex align-items-center">
                  <!-- Detail Button -->
                  <button
                    class="btn btn-sm btn-outline-danger ml-3"
                    @click.stop="$emit('open-detail', course, type)"
                  >
                    <i class="fe fe-eye" style="font-size: 0.8rem"></i>
                  </button>
                  <!-- Selection Checkbox -->
                  <input
                    v-if="isCanSelect"
                    type="checkbox"
                    class="form-check-input checkbox-lg cursor-pointer"
                    :checked="
                      forceSelect ||
                      selectedItems.some((i) => i.id === course.id)
                    "
                    :disabled="forceSelect"
                    @change="!forceSelect && onSelectChange(course, $event)"
                    aria-label="Select course"
                  />
                </div>
              </div>
            </div>
          </div>
          <!-- End Card Component -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { titleCase } from "@/utils/titleCase";
import { getApprovalStatusLabel } from "@/utils/approvalStatus";
import { getTrainingStatusClass } from "../utils/getTrainingStatusClass";
import { getTrainingTypeIcon } from "../utils/getTrainingTypeIcon";
import { truncateText } from "@/utils/truncateText";

export default {
  name: "HorizontalCardGrid",

  props: {
    isLoading: Boolean,
    activeTab: String,
    isCanSelect: {
      type: Boolean,
      default: false,
    },
    forceSelect: {
      type: Boolean,
      default: false,
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    imageBaseUrl: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "",
    },
    isFooter: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      canScrollLeft: false,
      canScrollRight: true,
    };
  },
  mounted() {
    this.$nextTick(this.handleScroll);
  },
  methods: {
    titleCase,
    getApprovalStatusLabel,
    truncateText,
    getTrainingStatusClass,
    getTrainingTypeIcon,
    scrollLeft() {
      const wrapper = this.$refs.scrollWrapper;
      if (wrapper) wrapper.scrollBy({ left: -320, behavior: "smooth" });
    },
    scrollRight() {
      const wrapper = this.$refs.scrollWrapper;
      if (wrapper) wrapper.scrollBy({ left: 320, behavior: "smooth" });
    },
    handleScroll() {
      const wrapper = this.$refs.scrollWrapper;
      if (!wrapper) return;
      this.canScrollLeft = wrapper.scrollLeft > 0;
      this.canScrollRight =
        wrapper.scrollLeft < wrapper.scrollWidth - wrapper.clientWidth - 1;
    },
    onSelectChange(course, e) {
      if (this.forceSelect) return;
      this.$emit("select-change", course, e.target.checked);
    },
  },
};
</script>

<style>
.horizontal-scroll-container {
  position: relative;
  padding: 0;
}

.horizontal-scroll-wrapper {
  overflow-x: auto;
  overflow-y: visible;
  display: flex;
  flex-wrap: nowrap;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.horizontal-scroll-wrapper::-webkit-scrollbar {
  display: none;
}

.scroll-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #374151;
  cursor: pointer;
  transition: all 0.3s ease;
}

.scroll-arrow.left {
  left: 0;
}

.scroll-arrow.right {
  right: 0;
}

.scroll-arrow:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.badge-custom {
  padding: 0.25rem 0.5rem;
  font-size: 0.65rem;
  font-weight: 600;
  border-radius: 0.25rem;
}

.checkbox-lg {
  width: 24px !important;
  height: 24px !important;
  cursor: pointer;
}
</style>
